import React from "react";
import { useCallback, useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../../root/routes";
import { usePrimaryOrganizationId } from "../../../../hooks";
import { Breadcrumbs, Button, Pill, ToolTip } from "@truevault/aquilo";
import { usePrivacyCenter } from "../../../../hooks/usePrivacyCenter";
import { usePrivacyCenterNotices } from "../../../../hooks/usePrivacyCenterNotice";
import { PrivacyCenterNoticeDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { ApiRouteHelpers } from "../../../../../common/root/apiRoutes";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { CompareVersionsModal } from "../../../../components/privacyCenter/CompareVersionsModal";
import { formatNoticeDate } from "../../../../formatters/date";

/**
 * Returns a stable version identifier for diff/API. New workflow uses versionName (e.g. "privacy-policy-20260206T1430").
 * Legacy notices without versionName use synthetic "privacy-policy-v{N}".
 */
const getVersionIdentifier = (notice: PrivacyCenterNoticeDto): string =>
  notice.versionName ?? `privacy-policy-v${notice.version}`;

/** Human-readable label from createdAt (when published), e.g. "Feb 18, 2026, 2:30 PM". */
const humanReadableName = (notice: PrivacyCenterNoticeDto): string => {
  const date = new Date(notice.createdAt);
  if (!isNaN(date.getTime())) {
    return formatNoticeDate(date);
  }
  return notice.versionName || `v${notice.version}`;
};

export const PrivacyPolicyDocumentsPage = () => {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter, privacyCenterRequest] = usePrivacyCenter(orgId);
  const [allNotices, allNoticesRequest] = usePrivacyCenterNotices(orgId, privacyCenter?.id);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareModalInitialVersion, setCompareModalInitialVersion] = useState<
    string | undefined
  >();

  const publishedNotices: PrivacyCenterNoticeDto[] = (allNotices || [])
    .filter(
      (notice: PrivacyCenterNoticeDto) =>
        notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
    )
    .sort((a: PrivacyCenterNoticeDto, b: PrivacyCenterNoticeDto) => {
      // Sort by createdAt (when published) descending (most recent first)
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA;
      // Tie-break by version number
      return b.version - a.version;
    });

  const handleDownload = useCallback(
    (notice: PrivacyCenterNoticeDto) => {
      if (!privacyCenter) return;
      const url = ApiRouteHelpers.organization.privacyCenter.notices.noticePdf(
        orgId,
        privacyCenter.id,
        notice.publicId,
      );
      window.open(url, "_blank");
    },
    [orgId, privacyCenter],
  );

  const canCompare = publishedNotices.length >= 2;

  return (
    <StandardPageWrapper
      requests={[privacyCenterRequest, allNoticesRequest]}
      className="tw-boundary"
    >
      <Breadcrumbs
        title="Privacy Policies"
        source="Privacy Docs"
        sourceRoute={Routes.documents.root}
      />

      {/* Header */}
      <div className="mt-4">
        <p className="text-sm text-gray-500">
          Published versions of your privacy policy are listed below. You can download individual
          versions or compare two versions to see what changed.
        </p>
      </div>

      {/* Empty state */}
      {publishedNotices.length === 0 && (
        <div className="mt-8 text-center">
          <div
            className="rounded-lg"
            style={{
              border: "1.5px dashed #726D83",
              background: "#F9F8FB",
              padding: "48px 24px",
            }}
          >
            <p className="text-lg font-medium text-gray-900">No published privacy policies yet</p>
            <p className="text-sm text-gray-500 mt-2">
              Once you publish a privacy policy, each version will appear here.
            </p>
          </div>
        </div>
      )}

      {/* Policy List */}
      {publishedNotices.length > 0 && (
        <div className="mt-6">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                <th className="pb-3">Document Published At</th>
                <th className="pb-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {publishedNotices.map((notice, index) => (
                <tr key={notice.publicId} className="hover:bg-gray-50">
                  <td className={index === 0 ? "pt-[12px] pb-[6px]" : "py-[6px]"}>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-normal text-gray-900">
                        {humanReadableName(notice)}
                      </p>
                      {index === 0 && (
                        <Pill variant="primary" noMargin>
                          Current
                        </Pill>
                      )}
                    </div>
                  </td>
                  <td className={index === 0 ? "pt-[12px] pb-[6px]" : "py-[6px]"}>
                    <div className="flex items-center justify-center gap-2">
                      <ToolTip text="Download PDF">
                        <Button
                          color="tertiary"
                          size="m"
                          width="auto"
                          variant="round"
                          isCircular
                          customSizeClassname="w-8 h-8 text-sm"
                          onClick={() => handleDownload(notice)}
                        >
                          <ArrowDownTrayIcon className="h-3 w-3 text-neutral-500" />
                        </Button>
                      </ToolTip>
                      {canCompare && (
                        <ToolTip text="Compare versions">
                          <Button
                            color="tertiary"
                            size="m"
                            width="auto"
                            variant="round"
                            isCircular
                            customSizeClassname="w-8 h-8 text-sm"
                            onClick={() => {
                              setCompareModalInitialVersion(getVersionIdentifier(notice));
                              setShowCompareModal(true);
                            }}
                          >
                            <img
                              src="/assets/images/text-compare.png"
                              alt="Compare versions"
                              className="h-3 w-3"
                            />
                          </Button>
                        </ToolTip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Compare Versions Modal */}
      {showCompareModal && (
        <CompareVersionsModal
          orgId={orgId}
          privacyCenterId={privacyCenter?.id}
          publishedNotices={publishedNotices}
          onClose={() => {
            setShowCompareModal(false);
            setCompareModalInitialVersion(undefined);
          }}
          initialVersionName={compareModalInitialVersion}
        />
      )}
    </StandardPageWrapper>
  );
};
